/**
 * Firebase 配置管理
 * 从接口获取配置，支持开发和生产环境
 */

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string
  databaseURL: string
}

export interface FirebaseEnvConfig {
  vapidKey: string
}

// 全局存储 Firebase 配置
let globalFirebaseConfig: FirebaseConfig | null = null
let globalFirebaseEnvConfig: FirebaseEnvConfig | null = null

/**
 * 设置 Firebase 配置（从接口获取后调用）
 */
export const setFirebaseConfig = (config: FirebaseConfig): void => {
  globalFirebaseConfig = config
}

/**
 * 设置 Firebase 环境配置（从接口获取后调用）
 */
export const setFirebaseEnvConfig = (config: FirebaseEnvConfig): void => {
  globalFirebaseEnvConfig = config
}

/**
 * 获取 Firebase 配置
 */
export const getFirebaseConfig = (): FirebaseConfig => {
  // 优先使用从接口获取的配置
  if (globalFirebaseConfig) {
    return globalFirebaseConfig
  }

  // 回退到环境变量（兼容性）
  const config: FirebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || ''
  }

  // 验证配置完整性
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId']
  const missingFields = requiredFields.filter(field => !config[field as keyof FirebaseConfig])

  if (missingFields.length > 0) {
    console.error('Firebase 配置缺失:', missingFields)
    throw new Error(`Firebase 配置不完整，缺失字段: ${missingFields.join(', ')}`)
  }

  return config
}

/**
 * 获取 Firebase 环境配置
 */
export const getFirebaseEnvConfig = (): FirebaseEnvConfig => {
  // 优先使用从接口获取的配置
  if (globalFirebaseEnvConfig) {
    return globalFirebaseEnvConfig
  }

  // 回退到环境变量（兼容性）
  return {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY || ''
  }
}

/**
 * 验证 Firebase 配置是否有效
 */
export const validateFirebaseConfig = (): boolean => {
  try {
    const config = getFirebaseConfig()
    const envConfig = getFirebaseEnvConfig()

    return !!(config.apiKey && config.projectId && envConfig.vapidKey)
  } catch (error) {
    // console.error('Firebase 配置验证失败:', error)
    return false
  }
}

/**
 * 获取当前环境信息
 */
export const getCurrentEnvironment = (): string => {
  return import.meta.env.MODE || 'development'
}

/**
 * 检查是否为生产环境
 */
export const isProduction = (): boolean => {
  return getCurrentEnvironment() === 'production'
}

/**
 * 检查是否为开发环境
 */
export const isDevelopment = (): boolean => {
  return getCurrentEnvironment() === 'development'
}
